import Typography from '@mui/material/Typography';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useTranslation } from 'react-i18next';
import { ITalent, TalentProcess, ProfileNetwork } from '@bloomays-lib/types.shared';
import { dateFormat, getProfileByNetwork } from '@bloomays-lib/utils.shared';
import { IconButton, Tag, ProfileCard } from '@bloomays-lib/ui.shared';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RawMainCard from '../atoms/MainCard';
import { styled } from '@mui/material/styles';
export type CardTalentProps = {
  talentProcess: TalentProcess;
  talent: ITalent;
};

const CardTalent = ({ talentProcess, talent }: CardTalentProps) => {
  const { t } = useTranslation(['talents']);
  const { firstname, lastname, jobTitle, location, availabilityDate } = talent;
  const { phase, emailMessagingAddress } = talentProcess;
  const linkedInUrl = talent.url || getProfileByNetwork(talent.profiles || [], ProfileNetwork.LinkedIn)?.url;

  let cleanedAvailableDate = 'TBC';
  if (availabilityDate) {
    cleanedAvailableDate = dateFormat(availabilityDate, 'dd/MM/yyyy');
  }

  let notValidatedClassesRoot: Record<string, unknown> | undefined;

  switch (phase) {
    case 'NotValidated':
    case 'Archive':
      notValidatedClassesRoot = { background: '#e0e0e0' };
      break;
    default:
      notValidatedClassesRoot = undefined;
  }

  const jobNameReduced = jobTitle?.length > 50 ? `${jobTitle?.substring(0, 50)}...` : jobTitle;

  return (
    <MainCard
      sx={notValidatedClassesRoot}
      title={
        <ProfileCard
          name={firstname && lastname ? `${firstname} ${lastname}` : 'TBC'}
          jobTitle={jobNameReduced}
          size="large"
          image={talent.image}
        />
      }
      content={true}
      contentSX={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2 }}
      action={
        <>
          {phase !== 'NotValidated' && emailMessagingAddress && (
            <>
              <IconButton
                variant="contained"
                color="secondary"
                aria-label="i don't like it"
                href={`mailto:${emailMessagingAddress}?subject=Ce%20profil%20ne%20m'interresse%20pas&body=Bonjour%2C%20Je%20ne%20souhaite%20pas%20rencontrer%20ce%20profil.%20Merci%20!`}
                target="_blank"
              >
                <ThumbDownIcon />
              </IconButton>
              <IconButton
                variant="contained"
                color="secondary"
                aria-label="it's my favorite"
                href={`mailto:${emailMessagingAddress}?subject=Ce%20profil%20m'interresse&body=Bonjour%2C%20Je%20souhaite%20rencontrer%20ce%20profil.%20Merci%20!`}
                target="_blank"
              >
                <ThumbUpIcon />
              </IconButton>
            </>
          )}
          {linkedInUrl && (
            <IconButton aria-label="linkedin" href={linkedInUrl} target="_blank">
              <LinkedInIcon />
            </IconButton>
          )}
        </>
      }
    >
      <TagContainer>
        <Tag text={phase ? t(`phase.${phase}`) : 'A positionner'} />
      </TagContainer>
      <InfoColumns>
        <InfoColumn>
          <InfoRow>
            <LocationOnIcon fontSize="small" sx={{ opacity: 0.7 }} />
            <Typography variant="body2" color="textSecondary">
              {location?.city || 'TBC'}
            </Typography>
          </InfoRow>
          <InfoRow>
            <CalendarTodayIcon fontSize="small" sx={{ opacity: 0.7 }} />
            <Typography variant="body2" color="textSecondary">
              {cleanedAvailableDate}
            </Typography>
          </InfoRow>
        </InfoColumn>
      </InfoColumns>
    </MainCard>
  ) as React.ReactNode;
};

const MainCard = styled(RawMainCard)({
  maxWidth: 300,
  minWidth: 300,
  maxHeight: 290,
  minHeight: 290,
  margin: '2em 18px',
});

const TagContainer = styled('div')(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
}));

const InfoColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
  gap: 4,
  width: '100%',
}));

const InfoColumns = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const InfoRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
}));

export default CardTalent;
export { CardTalent };
